import { AlertCircle, AlertTriangle, Lightbulb, CheckCircle2, ArrowRight } from "lucide-react";
import type { HiveWorkflow, WfIssue } from "@/types";
import { useWorkflow, validateWorkflow, workflowGatedProducts } from "@/store/workflowStore";
import { useHive } from "@/store/useHive";
import { APPS } from "@/data/apps";

const LEVEL = {
  error: { icon: AlertCircle, color: "#f43f5e", label: "Error" },
  warning: { icon: AlertTriangle, color: "#f59e0b", label: "Warning" },
  suggestion: { icon: Lightbulb, color: "#38bdf8", label: "Suggestion" },
} as const;

export function WorkflowValidationPanel({ wf }: { wf: HiveWorkflow }) {
  const selectNode = useWorkflow((s) => s.selectNode);
  const entitlements = useHive((s) => s.entitlements);
  // Entitlement findings lead the list — same rule the server enforces at
  // publish/resume time (workflow_product_not_enabled).
  const gatedIssues: WfIssue[] = workflowGatedProducts(wf, entitlements).map((p) => ({
    level: "error",
    text: `Contains ${APPS[p].name} steps — ${APPS[p].name} is Early Access for your organization. The workflow can be edited and test-run, but not activated. Request early access from the app grid.`,
  }));
  const issues = [...gatedIssues, ...validateWorkflow(wf)];
  const errors = issues.filter((i) => i.level === "error").length;
  const warnings = issues.filter((i) => i.level === "warning").length;
  const suggestions = issues.filter((i) => i.level === "suggestion").length;

  if (issues.length === 0)
    return (
      <div className="grid h-full place-items-center text-center">
        <div>
          <CheckCircle2 size={28} className="mx-auto text-emerald-500" />
          <div className="mt-2 text-sm font-medium text-app">All checks passed</div>
          <div className="text-xs text-app-2">This workflow is ready to publish.</div>
        </div>
      </div>
    );

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-app px-4 py-2 text-[11px]">
        <Count n={errors} color="#f43f5e" label="errors" />
        <Count n={warnings} color="#f59e0b" label="warnings" />
        <Count n={suggestions} color="#38bdf8" label="suggestions" />
        <span className="ml-auto text-app-2">{errors > 0 ? "Resolve errors before publishing" : "Safe to publish"}</span>
      </div>
      <div className="flex-1 space-y-1.5 overflow-y-auto no-scrollbar p-3">
        {issues.map((issue, i) => {
          const L = LEVEL[issue.level];
          return (
            <button
              key={i}
              onClick={() => issue.nodeId && selectNode(issue.nodeId)}
              className="group flex w-full items-center gap-2.5 rounded-lg border border-app bg-surface/40 px-3 py-2 text-left transition hover:border-app-strong"
            >
              <L.icon size={15} style={{ color: L.color }} className="shrink-0" />
              <span className="flex-1 text-xs text-app-1">{issue.text}</span>
              {issue.nodeId && (
                <span className="flex items-center gap-1 text-[10px] text-app-2 opacity-0 transition group-hover:opacity-100">
                  jump to node <ArrowRight size={11} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Count({ n, color, label }: { n: number; color: string; label: string }) {
  return (
    <span className="flex items-center gap-1 font-medium" style={{ color: n ? color : "hsl(var(--text-2))" }}>
      <span className="tabular-nums">{n}</span>
      <span className="text-app-2">{label}</span>
    </span>
  );
}
